
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ScrollToTop from '@/components/ScrollToTop';
import SEO from '@/components/SEO';

const PrivacyPolicy = () => {
  return (
    <div className="flex flex-col min-h-screen bg-dark-bg">
      <SEO
        title="Privacy Policy - SkyTest.ai"
        description="Learn about how SkyTest.ai collects, processes, and protects your personal data in accordance with GDPR"
      />
      <Navbar />
      
      <main className="flex-grow py-12 md:py-16 lg:py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gradient">Privacy Policy for Skytest.ai</h1>
          <p className="text-gray-400 mb-8">Last updated: 7 May 2025</p>
          
          <div className="prose prose-invert prose-lg max-w-none">
            <p className="text-gray-300">
              Skytest.ai ("we," "our," or "us") is committed to protecting your personal data and respecting your privacy in accordance with the General Data Protection Regulation (EU) 2016/679 ("GDPR"). This Privacy Policy explains how we collect, use, and safeguard your data when you visit https://www.skytest.ai (the "Site").
            </p>
            
            <hr className="my-8 border-dark-border" />
            
            <h2 className="text-2xl font-bold mt-8 mb-4 text-white">1. Data Controller</h2>
            <p className="text-gray-300">
              Skytest.ai is the data controller responsible for the processing of your personal data.
            </p>
            <p className="text-gray-300">
              If you have any questions or requests, please contact us at:<br />
              📧 <a href="mailto:info@oursky.com" className="text-gray-400 hover:text-brand-light transition-colors">info@oursky.com</a>
            </p>
            
            <hr className="my-8 border-dark-border" />
            
            <h2 className="text-2xl font-bold mt-8 mb-4 text-white">2. What Data We Collect and Why</h2>
            <p className="text-gray-300">
              We collect and process the following types of personal data:
            </p>
            
            <h3 className="text-xl font-bold mt-6 mb-3 text-white">a. Usage Data</h3>
            <p className="text-gray-300">
              When you visit our Site, we collect technical and usage data such as:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4 text-gray-300">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device type</li>
              <li>Pages visited</li>
              <li>Date and time of visit</li>
              <li>Referring URL</li>
            </ul>
            <p className="text-gray-300">
              <strong>Legal basis:</strong> Our legitimate interest (Article 6(1)(f) GDPR) in analyzing traffic to improve the functionality, security, and performance of our Site.
            </p>
            
            <h3 className="text-xl font-bold mt-6 mb-3 text-white">b. Contact Form Data</h3>
            <p className="text-gray-300">
              When you fill out our contact form, we collect:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4 text-gray-300">
              <li>Your name</li>
              <li>Email address</li>
              <li>Company name</li>
              <li>Website URL</li>
              <li>Message content (if applicable)</li>
            </ul>
            <p className="text-gray-300">
              <strong>Purpose:</strong> To respond to inquiries and provide relevant information or support.
            </p>
            <p className="text-gray-300">
              <strong>Legal basis:</strong> Your consent (Article 6(1)(a) GDPR) or, where applicable, steps prior to entering into a contract (Article 6(1)(b) GDPR).
            </p>
            
            <hr className="my-8 border-dark-border" />
            
            <h2 className="text-2xl font-bold mt-8 mb-4 text-white">3. Cookies and Google Analytics</h2>
            <p className="text-gray-300">
              We use cookies and similar technologies to improve your experience and analyze Site traffic.
            </p>
            <p className="text-gray-300">
              We use Google Analytics, provided by Google LLC, to collect usage statistics. Google Analytics uses cookies to gather data about how users interact with our website. This data is transferred to and stored on Google servers, which may be located outside the EU.
            </p>
            <p className="text-gray-300">
              <strong>Legal basis:</strong> Your consent, collected via our cookie banner (Article 6(1)(a) GDPR). You can withdraw your consent at any time by adjusting your cookie settings or using the Google Analytics Opt-out Add-on.
            </p>
            
            <hr className="my-8 border-dark-border" />
            
            <h2 className="text-2xl font-bold mt-8 mb-4 text-white">4. Automated Processing via n8n</h2>
            <p className="text-gray-300">
              We use n8n, a secure workflow automation tool, to handle and process contact form submissions. When you submit a form, the data is passed through n8n and routed to our internal team.
            </p>
            <p className="text-gray-300">
              <strong>Purpose:</strong> Efficiently manage and respond to your inquiries.<br />
              <strong>Legal basis:</strong> Your consent (Article 6(1)(a) GDPR).
            </p>
            <p className="text-gray-300">
              n8n processes data on our behalf and does not use it for its own purposes. A data processing agreement (DPA) is in place with n8n where required.
            </p>
            
            <hr className="my-8 border-dark-border" />
            
            <h2 className="text-2xl font-bold mt-8 mb-4 text-white">5. Data Sharing and International Transfers</h2>
            <p className="text-gray-300">
              We do not sell or rent your personal data. We may share it with:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4 text-gray-300">
              <li>Service providers (e.g., Google Analytics, n8n) under strict data processing agreements</li>
              <li>Legal authorities, if required by law or to protect our rights</li>
            </ul>
            <p className="text-gray-300">
              If your data is transferred outside the EU/EEA (e.g., to Google servers in the US), we ensure appropriate safeguards are in place, such as Standard Contractual Clauses (SCCs) approved by the European Commission.
            </p>
            
            <hr className="my-8 border-dark-border" />
            
            <h2 className="text-2xl font-bold mt-8 mb-4 text-white">6. Data Retention</h2>
            <p className="text-gray-300">
              We retain your personal data only for as long as necessary to fulfill the purposes we collected it for, including legal or reporting obligations.
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4 text-gray-300">
              <li>Contact form submissions: up to 12 months</li>
              <li>Google Analytics data: retained according to Google's standard policies (currently up to 26 months)</li>
            </ul>
            
            <hr className="my-8 border-dark-border" />
            
            <h2 className="text-2xl font-bold mt-8 mb-4 text-white">7. Your Rights Under GDPR</h2>
            <p className="text-gray-300">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4 text-gray-300">
              <li>Access your personal data (Art. 15)</li>
              <li>Rectify inaccurate data (Art. 16)</li>
              <li>Erase your data ("right to be forgotten") (Art. 17)</li>
              <li>Restrict or object to processing (Art. 18, 21)</li>
              <li>Withdraw consent at any time (Art. 7)</li>
              <li>Lodge a complaint with a supervisory authority (Art. 77)</li>
            </ul>
            <p className="text-gray-300">
              To exercise any of these rights, please contact us at: <a href="mailto:info@oursky.com" className="text-gray-400 hover:text-brand-light transition-colors">info@oursky.com</a>
            </p>
            
            <hr className="my-8 border-dark-border" />
            
            <h2 className="text-2xl font-bold mt-8 mb-4 text-white">8. Security</h2>
            <p className="text-gray-300">
              We take appropriate technical and organizational measures to protect your personal data from unauthorized access, loss, or misuse.
            </p>
            
            <hr className="my-8 border-dark-border" />
            
            <h2 className="text-2xl font-bold mt-8 mb-4 text-white">9. Third-Party Links</h2>
            <p className="text-gray-300">
              Our Site may contain links to external websites. We are not responsible for the privacy practices of those websites.
            </p>
            
            <hr className="my-8 border-dark-border" />
            
            <h2 className="text-2xl font-bold mt-8 mb-4 text-white">10. Updates to This Policy</h2>
            <p className="text-gray-300">
              We may update this Privacy Policy from time to time. The latest version will always be available on our website, with the effective date listed at the top.
            </p>
            
            <hr className="my-8 border-dark-border" />
            
            <h2 className="text-2xl font-bold mt-8 mb-4 text-white">11. Contact Us</h2>
            <p className="text-gray-300">
              For any questions about this Privacy Policy or your personal data, please contact us at:
            </p>
            <p className="text-gray-300">
              📧 <a href="mailto:info@oursky.com" className="text-gray-400 hover:text-brand-light transition-colors">info@oursky.com</a><br />
              🌐 <a href="https://www.skytest.ai" className="text-gray-400 hover:text-brand-light transition-colors">https://www.skytest.ai</a>
            </p>
          </div>
        </div>
      </main>
      
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default PrivacyPolicy;
